import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "@aws-amplify/auth";
import { SearchIcon } from "../../components/icons";
import swasthiqSymbol from "../../assets/kaagazy-symbol.png";

const SECTION_LABELS: Record<string, string> = {
  profile:      "Profile & Account",
  devices:      "Trusted Devices",
  clinic:       "Clinic Settings",
  access:       "Access & Personalizations",
  finance:      "Finance & Billing",
  privacy:      "Data & Privacy",
  import:       "Data Import",
  compliance:   "Compliance & Verification",
  system:       "System & Preferences",
  automation:   "Smart Automation",
};

interface SettingsHeaderProps {
  activeSection:   string;
  searchQuery:     string;
  onSearch:        (q: string) => void;
  onSubmitSearch?: () => void;        // Enter in the search box → jump to best match
  onProfile?:      () => void;        // avatar menu → open Profile & Account
  avatarUrl?:      string;
  userName?:       string;
}

export const SettingsHeader: React.FC<SettingsHeaderProps> = ({
  activeSection,
  searchQuery,
  onSearch,
  onSubmitSearch,
  onProfile,
  avatarUrl,
  userName,
}) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the avatar menu on outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const handleProfile = () => {
    setMenuOpen(false);
    onProfile?.();
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await signOut();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  return (
    <header className="bg-white border-b border-gray-100 flex-shrink-0">
      {/* Row 1 — persistent: title + search + avatar. On mobile the search box wraps
          onto its own full-width line between the title row and the avatar via
          flex-wrap + order, so it stays fully usable instead of being squeezed or hidden. */}
      <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-3 px-4 sm:px-6 py-3 border-b border-gray-50">
        <div className="order-1">
          <h1 className="text-base font-semibold text-gray-900">Settings</h1>
          <p className="text-xs text-gray-400 mt-0.5">Manage your clinic, staff, and system Preferences </p>
        </div>

        {/* Search — wraps to its own full-width line on mobile (order-3), stays inline on sm:+ (order-2) */}
        <div className="relative order-3 sm:order-2 w-full sm:w-64">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
            <SearchIcon />
          </span>
          <input
            type="text"
            placeholder="Search settings…"
            value={searchQuery}
            onChange={e => onSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") onSubmitSearch?.(); }}
            className="w-full pl-8 pr-3 py-2 sm:py-1.5 text-base sm:text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
          />
        </div>

        {/* Avatar + dropdown menu */}
        <div className="relative order-2 sm:order-3 flex-shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen(o => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title={userName ?? "Account"}
              className="block rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {/* User photo if uploaded, else the SwasthiQ logo (no initials). */}
              <img
                src={avatarUrl || swasthiqSymbol}
                alt={userName ?? "avatar"}
                className="w-8 h-8 rounded-full object-cover bg-white ring-2 ring-white"
              />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg ring-1 ring-black/5 py-1 z-20"
              >
                {userName && (
                  <div className="px-3 py-2 border-b border-gray-50">
                    <p className="text-xs font-medium text-gray-900 truncate">{userName}</p>
                  </div>
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleProfile}
                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="text-gray-400">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Profile
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      {/* Row 2 — breadcrumb changes with active section */}
      <div className="px-4 sm:px-6 py-2">
        <p className="text-xs text-gray-400">
          <span className="text-gray-500 font-medium">Settings</span>
          <span className="mx-1.5 text-gray-300">/</span>
          <span className="text-blue-600 font-medium">
            {SECTION_LABELS[activeSection] ?? activeSection}
          </span>
        </p>
      </div>
    </header>
  );
};
